import React from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { Box, Button, IconButton, Typography } from "@mui/material";

import CloseIcon from "icons/Close";

import { LINKS, STRIPE_SUBSCRIPTION_TYPES } from "@constants";
import useModal from "hooks/useModal";
import { checkoutSession } from "actions/stripe";
import Modal from "components/common/Modal";
import { MODALS, UPGRADE_PLAN_PLACE } from "components/common/Modal/utils";
import eventsService, { EVENTS } from "services/events";
import { usePlanInfo } from "hooks/usePlanInfo";

const modalId = MODALS.upgradePlan;

const UpgradePlan = () => {
  const dispatch = useDispatch();
  const [open, data, handleClose] = useModal({ modalId });
  const { isTrialExpired } = useSelector((state) => state.auth);
  const { isPerCredits } = usePlanInfo();

  if (!open) {
    return <></>;
  }

  // default is UPGRADE_PLAN_PLACE.trial_expired
  let title = "Trial Plan Expired? Upgrade Now!";
  let description = (
    <>
      Your trial plan has ended. Unlock full features by upgrading your plan
      now.
    </>
  );
  let img = "/images/upgrade-plan-expired.png";
  let upgradeBtnText = "Upgrade plan";

  if (data?.type && (!isTrialExpired || isPerCredits)) {
    if (data.type === UPGRADE_PLAN_PLACE.download) {
      title = "Unlock Download Feature";
      description = <>Upgrade from Trial to access the download feature.</>;
      img = "/images/upgrade-plan-download.png";
      upgradeBtnText = "Upgrade to Unlock";
    }
    if (data.type === UPGRADE_PLAN_PLACE.voice) {
      title = "Unlock Extra Voice";
      description = (
        <>
          Access a voice available in higher-tier plans along with 100+ others.
          Upgrade now for access
        </>
      );
      img = "/images/upgrade-plan-voice-accent.png";
      upgradeBtnText = "Upgrade to Unlock";
    }
    if (data.type === UPGRADE_PLAN_PLACE.accent) {
      title = "Discover New Accent!";
      description = (
        <>
          Unlock accent in greater plans. Upgrade now for this voice and more.
        </>
      );
      img = "/images/upgrade-plan-voice-accent.png";
      upgradeBtnText = "Upgrade to Unlock";
    }
    if (data.type === UPGRADE_PLAN_PLACE.out_of_minutes) {
      title = "Out of Minutes? Upgrade for More!";
      description = (
        <>
          Running low on minutes? Get 10x more with an <b>upgrade</b> or{" "}
          <b>purchase credits</b>.
        </>
      );
      img = "/images/upgrade-plan-minutes.png";
      upgradeBtnText = "Upgrade Plan";
    }
    if (data.type === UPGRADE_PLAN_PLACE.out_of_credits) {
      title = "Out of Credits! Upgrade Now";
      description = (
        <>
          You're low on credits. <b>Upgrade</b> your plan or <b>buy more</b>.
        </>
      );
      img = "/images/upgrade-plan-credits.png";
      upgradeBtnText = "Buy Credits";
    }
    if (data.type === UPGRADE_PLAN_PLACE.out_of_characters) {
      title = "Out of Characters! Upgrade Now";
      description = (
        <>
          Running low on characters? Get more with an <b>upgrade</b> or{" "}
          <b>purchase</b> credits.
        </>
      );
      img = "/images/upgrade-plan-credits.png";
      upgradeBtnText = "Upgrade Plan";
    }
    if (data.type === UPGRADE_PLAN_PLACE.unlock_sts) {
      title = "Unlock STS Conversions";
      description = (
        <>
          STS conversions aren't included in this plan. Upgrade to enable this
          feature.
        </>
      );
      img = "/images/upgrade-plan-sts.png";
      upgradeBtnText = "Upgrade Plan";
    }
  }

  const handleBuyCredits = () => {
    eventsService.track(EVENTS.USER_PLAN_CARD, {
      place: "out_of_minutes_credits",
    });
    dispatch(checkoutSession({ price: STRIPE_SUBSCRIPTION_TYPES.credits }));
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      maxWidth="320px"
      contentSx={{
        p: "16px",
        // height: "510px",
      }}
    >
      <IconButton
        variant="outlined"
        onClick={handleClose}
        sx={{
          position: "absolute",
          top: "16px",
          right: "16px",
          zIndex: 1,
          background: "var(--surface-elements-elevation-high)",
        }}
      >
        <CloseIcon />
      </IconButton>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
          pt: "48px",
          // overflowY: "scroll",
          // height: "calc(100% - 64px)",
        }}
      >
        <img
          src={img}
          loading="lazy"
          width="280"
          height="200"
          style={{
            objectFit: "contain",
          }}
        />
        <Typography
          variant="h1"
          sx={{
            color: "var(--on-surface-primary)",
            fontSize: "24px",
            lineHeight: "32px",
            width: "100%",
          }}
        >
          {title}
        </Typography>
        <Typography variant="body2">{description}</Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          mt: "16px",
          gap: "8px",
        }}
      >
        <Button
          variant="contained"
          onClick={() => handleClose()}
          sx={{ flexGrow: 1 }}
          component={Link}
          to={LINKS.plan}
        >
          {upgradeBtnText}
        </Button>
      </Box>
    </Modal>
  );
};

export default UpgradePlan;
